import React from 'react';

import AutoEnrolIcon from 'icons/double-arrow-rotate.svg';
import LogoIcon from 'icons/ihasco-logo-mark.svg';
import pluralize from 'pluralize';
import { useTranslation } from 'react-i18next';
import voca from 'voca';

import PersonalInfo from 'components/application/PersonalInfo';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useTrainingRegisterResources } from 'components/contexts/TrainingRegisterResourceManagementContext';

import {
  currencyNumberToCurrencyString,
  displayCurrencyString,
} from 'components/helpers/currency';
import { formatDuration } from 'components/helpers/dates';
import {
  isCourseEligibleForAutoEnrolment,
  isELearningAvailable,
} from 'components/helpers/resources/courses';
import { resourceShape } from 'components/helpers/serialisableResources';

export default function CourseSidePanelDetailsTab({
  course,
  eLearningCourse,
  originalCourse,
}) {
  const currentActor = useCurrentActor();

  const { isAutoEnrolmentIncludingMissingTraining } =
    useTrainingRegisterResources();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'courses.sidepanel.CourseSidePanelDetailsTab',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const { currencyCode } = currentActor.division.attributes;

  const {
    defaultBookingNote,
    expires,
    learningMethod,
    provider,
    renewalFrequency,
    renewalInterval,
    requiresEvidence,
    timeAmount,
    timeInterval,
  } = course.attributes;

  const timeEstimate =
    eLearningCourse ?
      eLearningCourse.attributes.durationParts ?
        formatDuration({
          durationParts: eLearningCourse.attributes.durationParts,
        })
      : ''
    : voca.isBlank(timeAmount) ? ''
    : formatDuration({
        durationParts: { [pluralize(timeInterval)]: timeAmount },
      });

  const isELearningCustom = eLearningCourse?.attributes.custom;

  const customELearningCourseCost =
    isELearningCustom &&
    currencyNumberToCurrencyString({
      number: eLearningCourse.attributes.cost,
      currencyCode,
    });

  const eLearningCourseCost =
    isELearningCustom ?
      displayCurrencyString({ string: customELearningCourseCost, currencyCode })
    : i18nT('eLearning.cost');

  const courseCostEstimate = currencyNumberToCurrencyString({
    number: course.attributes.costEstimate,
    currencyCode,
  });

  const costEstimate =
    eLearningCourse ? eLearningCourseCost : (
      displayCurrencyString({ string: courseCostEstimate, currencyCode })
    );

  return (
    <div className='popup__body-content popup__body-content--border-top tw-border-grey-100'>
      {isELearningAvailable({ currentActor, course, originalCourse }) && (
        <div className='tw-mb-6 tw-flex tw-h-16 tw-rounded-md tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white tw-p-3'>
          <div className='tw-mr-2'>
            <LogoIcon height={20} width={20} />
          </div>
          <div>
            <div className='tw-font-medium'>
              {i18nTCommon('courses.eLearningAvailable')}
            </div>
            <div>{i18nT('eLearning.description')}</div>
          </div>
        </div>
      )}
      <PersonalInfo
        label={i18nTCommon('courses.renewalFrequency')}
        text={
          expires ?
            voca.isBlank(renewalFrequency) ?
              ''
            : formatDuration({
                durationParts: {
                  [pluralize(renewalInterval)]: renewalFrequency,
                },
              })

          : i18nTCommon('shared.doesNotExpire')
        }
      />
      <PersonalInfo
        label={i18nTCommon('shared.evidence')}
        text={
          requiresEvidence ?
            i18nTCommon('shared.required')
          : i18nTCommon('shared.notRequired')
        }
      />
      <div className='personal-info'>
        <div className='personal-info__info-container'>
          <div className='personal-info__label tw-font-medium tw-text-grey-900'>
            {i18nTCommon('shared.method')}
          </div>
          <div className='personal-info__text'>
            {(
              isCourseEligibleForAutoEnrolment({
                currentActor,
                isAutoEnrolmentIncludingMissingTraining,
                course,
              })
            ) ?
              <div className='tw-flex tw-items-center tw-justify-between'>
                <span>
                  {i18nTCommon('courses.learningMethods.eLearning')} (
                </span>
                <AutoEnrolIcon className='[&_path]:tw-fill-grey-700' />
                <span>{i18nTCommon('courses.autoEnrols')})</span>
              </div>
            : <span>
                {learningMethod &&
                  i18nTCommon(
                    `courses.learningMethods.${voca.camelCase(learningMethod)}`,
                  )}
              </span>
            }
          </div>
        </div>
      </div>
      <PersonalInfo label={i18nTCommon('courses.provider')} text={provider} />
      <PersonalInfo
        label={i18nTCommon('courses.costEstimate')}
        text={costEstimate}
      />
      <PersonalInfo
        label={i18nTCommon('courses.timeEstimate')}
        text={timeEstimate}
      />
      {!eLearningCourse && (
        <PersonalInfo
          isTruncated={false}
          label={i18nTCommon('courses.defaultBookingNotes')}
          text={defaultBookingNote}
        />
      )}
    </div>
  );
}

CourseSidePanelDetailsTab.propTypes = {
  course: resourceShape('course').isRequired,
  eLearningCourse: resourceShape('eLearningCourse'),
  originalCourse: resourceShape('originalCourse'),
};
